// mission-initializer.ts
//
// `avatar autopilot init --request-file <path>` (spec §3.3 Req 1, tests 6-15).
// Validates InitRequestSchema (rejects extra fields incl. self-declared
// approval — .strict() already does this), exclusive init lock, idempotent by
// a derived operation id, loads effective-config and threads
// permissions_allow_fallback into mission-workspace.ts, seeds survey+plan
// tickets, creates pending G0/G1, writes compatibility.json. NEVER commits
// beyond P3's existing empty-base-commit plumbing.
//
// Idempotency key (documented judgment call in evidence): InitRequestSchema
// has NO explicit operation_id field. This module derives one deterministically
// as sha256 of the canonical (sorted-key) JSON of the VALIDATED request —
// same request content (incl. same blueprint hash/repo/base) => same
// operation id => a killed-and-rerun init recognizes the mission it already
// started (by checking whether `<mission_home>` for that operation id already
// exists) instead of creating a second worktree/mission (tests 10/15).

import { randomUUID } from "node:crypto";
import { ensureDir, pathExists } from "../filesystem-helpers.js";
import { readCliVersion } from "../read-cli-version-from-package-json.js";
import { loadEffectiveConfig } from "./effective-config-loader.js";
import { readGateFile } from "./gate-registry.js";
import { InitValidationError } from "./mission-initializer-errors.js";
import { acquireInitLock } from "./mission-initializer-lock.js";
import { writeInitialPinBaseline } from "./mission-initializer-pin-baseline.js";
import { findInitConfigProblem, runMissionInitPreflight } from "./mission-initializer-preflight.js";
import { persistInitProvenance } from "./mission-initializer-provenance.js";
import { registerInitializedMission } from "./mission-initializer-registration.js";
import { resolveInitRequest } from "./mission-initializer-request.js";
import { seedBootstrapTickets } from "./mission-initializer-tickets.js";
import { MissionStore } from "./mission-store.js";
import { createMissionWorkspace } from "./mission-workspace.js";
import { listPendingGateRequests, type PendingGateRequest } from "./status-operating-table.js";

// Re-exported so existing call sites (and tests) that import id derivation /
// the validation error from mission-initializer.ts keep working after the split.
export { deriveOperationId } from "./mission-initializer-ids.js";
export { InitValidationError };

export interface MissionInitializerDeps {
  now?: () => Date;
  cliVersion?: string;
  probeClaudeVersion?: () => Promise<{ path: string; version: string; hash: string }>;
  /** Probe cho tuple pin (R-24); mặc định probe `claude --version` của ops wiring. */
  probePinClaudeVersion?: () => Promise<string>;
}

export interface InitMissionInput {
  requestFilePath: string;
  workspaceRoot: string;
  packSourceDir: string;
  actorOsAccount: string;
  /** `~/.avatar/autopilot-config.json` — injected, never derived from workspaceRoot. */
  globalConfigPath: string;
  deps?: MissionInitializerDeps;
}

export interface InitMissionReceipt {
  missionId: string;
  operationId: string;
  controlRoot: string;
  missionWorkspace: string;
  idempotent: boolean;
  pending_gates: PendingGateRequest[];
}

/**
 * Initialize a mission. Idempotent: rerunning with the SAME request content
 * (same derived operation id) when the mission already exists returns the
 * existing mission's receipt (idempotent=true) instead of creating a second
 * worktree/mission.
 */
export async function initMission(input: InitMissionInput): Promise<InitMissionReceipt> {
  const now = input.deps?.now ?? (() => new Date());

  // 1. Validate request + derive operation/mission id and paths — tách sang
  // mission-initializer-request.ts (phase 10, lô A); xem đó cho lý do idempotency key.
  const { request, operationId, missionId, controlRoot, missionWorkspace, compatPath } =
    await resolveInitRequest(input.requestFilePath, input.workspaceRoot, now);
  // R-08: one helper for `pending_gates`, shared by both `return`s below.
  const receipt = async (idempotent: boolean): Promise<InitMissionReceipt> => {
    const pending_gates = listPendingGateRequests(await readGateFile(controlRoot));
    return { missionId, operationId, controlRoot, missionWorkspace, idempotent, pending_gates };
  };

  // 1b. [B1] Preflight (base_sha -> blueprint hash+non-goals -> pack playbook)
  // BEFORE ensureDir/lock/journal, so a rejected request leaves no
  // `missions/<id>/` behind. Skipped when THIS request already completed init.
  if (!(await pathExists(compatPath))) {
    const problem = await runMissionInitPreflight({
      request,
      packSourceDir: input.packSourceDir,
      layout: { workspaceRoot: input.workspaceRoot, worktreePath: missionWorkspace },
    });
    if (problem !== undefined) throw new InitValidationError(problem);
    // Config too, before ensureDir: a bad config used to leave `missions/<id>/`.
    const configProblem = await findInitConfigProblem(input.globalConfigPath);
    if (configProblem !== undefined) throw new InitValidationError(configProblem);
  }

  // 2. Idempotent short-circuit (self-heals a baseline missed by crash mid-8/8b).
  const pinBaselineInput = {
    missionWorkspace,
    controlRoot,
    now,
    probeClaudeVersion: input.deps?.probePinClaudeVersion,
  };
  await ensureDir(controlRoot);
  const releaseLock = await acquireInitLock(controlRoot, operationId);
  try {
    if (await pathExists(compatPath)) {
      await writeInitialPinBaseline({ ...pinBaselineInput, onlyIfMissing: true });
      return receipt(true);
    }

    // 3. Load effective-config (same loader `run` uses) — thread
    // permissions_allow_fallback into mission-workspace.ts (N18).
    const effCfg = await loadEffectiveConfig({
      globalConfigPath: input.globalConfigPath,
      controlRoot,
    });
    if (!effCfg.ok) throw new InitValidationError(`config_invalid: ${effCfg.message}`);

    // 4. Journal: operation_intent BEFORE side effects.
    const store = await MissionStore.open(controlRoot);
    try {
      await store.commit([
        {
          event_id: randomUUID(),
          operation_id: operationId,
          delta: { kind: "operation_intent", operation_id: operationId, operation_kind: "init" },
        },
      ]);

      // 5. Build mission workspace (worktree, incl. empty-HEAD repos, pack pin,
      // settings profile, ignore entries) — P3, handles ALL of this.
      // mission-workspace.ts writes `.claude/*` directly under `worktreePath`
      // (see its steps 2-4) — the git worktree root IS the `.claude`-bearing
      // mission workspace; there's no separate `src/<repo>` nesting.
      const workspaceResult = await createMissionWorkspace({
        sourceRepo: request.repo.source_repo,
        worktreePath: missionWorkspace,
        controlRoot,
        workspaceRoot: input.workspaceRoot,
        missionId,
        packSourceDir: input.packSourceDir,
        permissionsAllowFallback: effCfg.config.permissions_allow_fallback,
        now,
        baseSha: request.repo.base_sha.toLowerCase(),
      });

      // 6. Seed survey (brownfield only) + plan tickets with idempotency keys.
      await seedBootstrapTickets(workspaceResult.worktree.worktreePath, request.mode);

      // 6b-6d: actor binding + declared resources + blueprint binding — tách
      // sang mission-initializer-provenance.ts (phase 10, lô A); xem đó để biết
      // lý do từng bước (Bug J, P15, P19b, P25, B3).
      // N1 (approvers[0] only) is ALSO encoded in mattermost-transport-preconditions.ts — change both together.
      const boundMattermostUserId = effCfg.config.mattermost.approvers[0]?.user_id;
      await persistInitProvenance({
        controlRoot,
        request,
        actorOsAccount: input.actorOsAccount,
        boundMattermostUserId,
        now,
      });

      // 7-8b: pending G0/G1 gates, compatibility manifest, baseline tuple pin —
      // tách sang mission-initializer-registration.ts (phase 10, lô A).
      await registerInitializedMission({
        controlRoot,
        missionWorkspace,
        operationId,
        request,
        cliVersion: input.deps?.cliVersion ?? readCliVersion(),
        settingsProfileSha256: workspaceResult.settingsProfileSha256,
        permissionsAllowFallback: effCfg.config.permissions_allow_fallback,
        now,
        probeClaudeVersion: input.deps?.probeClaudeVersion,
        probePinClaudeVersion: input.deps?.probePinClaudeVersion,
      });

      // 9. operation_result — no `commit` op recorded (test 15: init never commits).
      await store.commit([
        {
          event_id: randomUUID(),
          operation_id: operationId,
          delta: { kind: "operation_result", operation_id: operationId, outcome: "done" },
        },
      ]);
    } finally {
      await store.close();
    }

    return receipt(false);
  } finally {
    await releaseLock();
  }
}
